import { useState } from "react";
import { toast } from "sonner";
import { Mail, Trash2, Loader2, ShieldAlert, Lock, Save, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AccountSettings() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();

  const [newEmail, setNewEmail] = useState("");
  const [emailPwd, setEmailPwd] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [delPwd, setDelPwd] = useState("");
  const [deleting, setDeleting] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !emailPwd) {
      toast.error("Renseignez la nouvelle adresse et votre mot de passe.");
      return;
    }
    setSavingEmail(true);
    try {
      const res = await api.post("/auth/change-email", { new_email: newEmail.trim(), password: emailPwd });
      if (res.data?.access_token) localStorage.setItem("ant_token", res.data.access_token);
      updateUser(res.data.user);
      setNewEmail("");
      setEmailPwd("");
      toast.success("Adresse email modifiée ! Un email de confirmation vient d'être envoyé (pensez à vérifier vos spams).");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de la modification");
    } finally {
      setSavingEmail(false);
    }
  };

  const confirmDelete = async () => {
    if (!delPwd) {
      toast.error("Saisissez votre mot de passe pour confirmer.");
      return;
    }
    setDeleting(true);
    try {
      await api.post("/auth/delete-account", { password: delPwd });
      toast.success("Votre compte a été supprimé.");
      logout();
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de la suppression");
      setDeleting(false);
    }
  };

  const input = "w-full bg-[#0F1812] border border-[#00E676]/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-[#697C6F] focus:outline-none focus:ring-2 focus:ring-[#39FF14]";

  return (
    <div className="mt-10" data-testid="account-settings">
      <h2 className="text-lg font-bold mb-4">Paramètres du compte</h2>

      {/* Change email */}
      <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-5 sm:p-6">
        <h3 className="font-bold flex items-center gap-2"><Mail size={17} className="text-[#00E676]" /> Modifier mon adresse email</h3>
        <p className="text-xs text-[#697C6F] mt-1">Adresse actuelle : <b className="text-[#A1B5A8]">{user.email}</b>. Un email de confirmation sera envoyé à la nouvelle adresse.</p>
        <form onSubmit={submitEmail} className="mt-4 space-y-3">
          <input type="email" className={input} placeholder="Nouvelle adresse email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="new-email-input" />
          <input type="password" className={input} placeholder="Votre mot de passe actuel" value={emailPwd} onChange={(e) => setEmailPwd(e.target.value)} data-testid="email-password-input" />
          <button type="submit" disabled={savingEmail} data-testid="save-email-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold text-[#04180a] bg-gradient-to-r from-[#00E676] to-[#39FF14] disabled:opacity-60">
            {savingEmail ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Enregistrer la nouvelle adresse
          </button>
        </form>
      </div>

      {/* Delete account */}
      {user.role !== "admin" && (
      <div className="mt-5 rounded-xl border border-[#FF3B30]/30 bg-[#1A0F0E] p-5 sm:p-6">
        <h3 className="font-bold flex items-center gap-2 text-[#FF6B60]"><ShieldAlert size={17} /> Zone dangereuse</h3>
        <p className="text-xs text-[#C89A96] mt-1">La suppression de votre compte est <b>définitive et irréversible</b>. Toutes vos données (paris, points, messages) seront effacées.</p>

        {!showDelete ? (
          <button onClick={() => setShowDelete(true)} data-testid="delete-account-btn"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold text-white bg-[#FF3B30]/90 hover:bg-[#FF3B30] transition-colors">
            <Trash2 size={16} /> Supprimer mon compte
          </button>
        ) : (
          <div className="mt-4 rounded-lg border border-[#FF3B30]/40 bg-[#0F1812] p-4" data-testid="delete-confirm-panel">
            <p className="text-sm font-bold text-[#FF6B60] mb-1">Êtes-vous vraiment sûr ?</p>
            <p className="text-xs text-[#C89A96] mb-3">Cette action ne peut pas être annulée. Saisissez votre mot de passe pour confirmer.</p>
            <div className="relative">
              <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#697C6F]" />
              <input type="password" className={`${input} pl-9`} placeholder="Mot de passe" value={delPwd} onChange={(e) => setDelPwd(e.target.value)} data-testid="delete-password-input" />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={confirmDelete} disabled={deleting} data-testid="confirm-delete-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold text-white bg-[#FF3B30] hover:bg-[#e0332a] disabled:opacity-60">
                {deleting ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />} Oui, supprimer définitivement
              </button>
              <button onClick={() => { setShowDelete(false); setDelPwd(""); }} data-testid="cancel-delete-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-white bg-[#131F17] border border-[#00E676]/20">
                <X size={16} /> Annuler
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
